import { useState, useEffect } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { Modal } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import { FaChevronUp, FaChevronDown } from "react-icons/fa";
import { IoCloseSharp } from "react-icons/io5";
import { HiXMark } from "react-icons/hi2";
import { baseurl } from "../BaseUrl/Baseurl";
import useSessionState from "./useSessionState";
import "./Commoncss.css";

const FORM_KEYS = [
  "subdivstate",
  "subdivsubdivname",
  "subdivselectedOptions",
  "subdivlocationDescription",
];

const validate = ({ subdivname, state, areas }) => {
  if (!subdivname) return "Please Enter Sub-Division Name";
  if (!state) return "Please Select A State";
  if (!areas || areas.length === 0) return "Please Select An Area";
  return "";
};

const SubDivisionCreateModal = ({ status, btnClick, onSuccess, onError }) => {
  const userData = useSelector((s) => s.userDetails);

  // form state (persisted so a refresh doesn't lose it)
  const [subdivname, setSubdivname] = useSessionState("subdivsubdivname", "");
  const [state, setState] = useSessionState("subdivstate", "");
  const [selectedOptions, setSelectedOptions] = useSessionState("subdivselectedOptions", []);
  const [locationDescription, setLocationDescription] = useSessionState("subdivlocationDescription", "");

  // option lists
  const [areaOptions, setAreaOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);

  // ui state
  const [search, setSearch] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const headers = {
    "Content-Type": "application/json",
    Authorization: "Bearer " + userData.token,
  };

  useEffect(() => {
    const load = (field, setter, extraParams = "") =>
      axios({
        method: "GET",
        url: `${baseurl}/api/get/list?field=${field}&userId=${userData.userId}${extraParams}&isSortingRequired=2`,
        headers,
      })
        .then((res) => setter(res.data.message))
        .catch(console.error);

    load("area", setAreaOptions, "&skipAlreadyAssigned=1");
    load("state", setStateOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearForm = () => {
    setSubdivname("");
    setState("");
    setSelectedOptions([]);
    setLocationDescription("");
    FORM_KEYS.forEach((k) => sessionStorage.removeItem(k));
  };

  const handleClose = () => {
    clearForm();
    btnClick();
  };

  const filteredOptions = areaOptions.filter((o) =>
    o.name.toLowerCase().includes(search.toLowerCase()),
  );

  const allFilteredSelected =
    filteredOptions.length > 0 &&
    filteredOptions.every((o) => selectedOptions.includes(o.id));

  const toggleOption = (id) =>
    setSelectedOptions((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const toggleAll = (checked) =>
    setSelectedOptions(checked ? filteredOptions.map((o) => o.id) : []);

  const handleSubmit = (e) => {
    e.preventDefault();

    const error = validate({ subdivname, state, areas: selectedOptions });
    if (error) {
      onError(error);
      return;
    }

    axios({
      method: "POST",
      url: `${baseurl}/api/list/add?userId=${userData.userId}&field=subDivision`,
      headers,
      data: {
        subDivision: subdivname,
        areaId: selectedOptions,
        stateId: Number.parseInt(state, 10),
        ...(locationDescription && locationDescription.trim() ? { locationDescription: locationDescription.trim() } : {}),
      },
    })
      .then((res) => {
        onSuccess(res.data.message);
        handleClose();
      })
      .catch((err) => {
        onError(err.response?.data?.message || "Something went wrong");
      });
  };

  return (
    <Modal show={!!status} onHide={handleClose} className="region-create-modal" centered>
      <Modal.Body className="bg-white">
        <div className="d-flex justify-content-between align-items-center">
          <h6 className="mb-0">Create New Sub-division</h6>
          <button type="button" className="btn p-0" onClick={handleClose}>
            <IoCloseSharp />
          </button>
        </div>
        <hr className="my-2" />

        <form className="p-2" onSubmit={handleSubmit} autoComplete="off">
          {/* Sub-division name */}
          <label className="form-label" htmlFor="subdivname-input">
            Sub-Division Name
          </label>
          <input
            type="text"
            className="project-box"
            id="subdivname-input"
            name="subdivname"
            placeholder="Enter Sub Division"
            value={subdivname}
            maxLength={100}
            onChange={(e) => {
              const val = e.target.value;
              setSubdivname(val.length > 100 ? val.slice(0, 100) : val);
            }}
            autoComplete="off"
          />

          {/* State */}
          <label className="form-label" htmlFor="state-select">
            Choose State
          </label>
          <select
            id="state-select"
            name="state"
            className="project-box"
            value={state}
            onChange={(e) => setState(e.target.value)}
          >
            <option value="">Select</option>
            {stateOptions.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>

          {/* Areas (multi-select) */}
          <label className="form-label" htmlFor="area-dropdown">
            Choose Area
          </label>
          <div className="mx-auto subdiv-area-field">
            <button
              id="area-dropdown"
              type="button"
              className="project-box rounded-md p-1 d-flex align-items-center justify-content-between"
              style={{
                backgroundColor: "#f1f5fb",
                border: "2px solid #f1f5fb",
              }}
              onClick={() => setDropdownOpen((o) => !o)}
            >
              <span className="flex-area subdiv-scrollbar p-1 arealist-scrollable-container">
                {selectedOptions.length > 0 ? (
                  selectedOptions.map((id) => {
                    const option = areaOptions.find((o) => o.id === id);
                    return (
                      <div
                        key={id}
                        className="d-inline-flex align-items-center rounded-pill p-1 justify-content-center light-blue-bg"
                      >
                        <span className="text-nowrap ellipsis-text">
                          {option ? option.name : id}
                        </span>
                        <button
                          type="button"
                          className="btn d-flex align-items-center p-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleOption(id);
                          }}
                        >
                          <HiXMark />
                        </button>
                      </div>
                    );
                  })
                ) : (
                  <span>Select </span>
                )}
              </span>
              <span className="d-flex m-0 justify-content-end">
                {dropdownOpen ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
              </span>
            </button>

            <AnimatePresence>
              {dropdownOpen && (
                <motion.div
                  initial={{ opacity: 0, height: 0, y: -6 }}
                  animate={{ opacity: 1, height: "auto", y: 0 }}
                  exit={{ opacity: 0, height: 0, y: -6 }}
                  transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                  className="bg-white border rounded-md mt-1 w-full subdiv-area-dropdown"
                >
                  <input
                    type="text"
                    placeholder="Search..."
                    value={search}
                    maxLength={100}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-100 px-3 py-1"
                    autoComplete="off"
                  />

                  <div className="scrollbar scrollable-box-subdiv w-100" id="style-1">
                    {filteredOptions.length > 0 ? (
                      <>
                        <div className="px-2 py-2 flex-area1">
                          <label className="checkbox-container" aria-label="Select All">
                            <input
                              className="custom-checkbox"
                              type="checkbox"
                              checked={allFilteredSelected}
                              onChange={(e) => toggleAll(e.target.checked)}
                            />
                            <span className="checkmark"></span>
                          </label>
                          <span>Select All</span>
                        </div>

                        {filteredOptions.map((option) => (
                          <div key={option.id} className="border-b">
                            <div className="px-2 py-2">
                              <div className="flex-area1">
                                <label className="checkbox-container" aria-label={option.name}>
                                  <input
                                    className="custom-checkbox"
                                    type="checkbox"
                                    checked={selectedOptions.includes(option.id)}
                                    onChange={() => toggleOption(option.id)}
                                  />
                                  <span className="checkmark"></span>
                                </label>
                                <span
                                  className={
                                    selectedOptions.includes(option.id)
                                      ? "sub-option-selected"
                                      : ""
                                  }
                                >
                                  {option.name}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </>
                    ) : (
                      <div className="px-3 py-2 text-center text-muted fsi-12">
                        No Data Available
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Location Description */}
          <label className="form-label mt-2" htmlFor="location-description-input">
            Location Description
          </label>
          <input
            type="text"
            className="project-box"
            id="location-description-input"
            name="locationDescription"
            placeholder="Enter Location Description"
            value={locationDescription}
            maxLength={100}
            onChange={(e) => {
              const val = e.target.value;
              setLocationDescription(val.length > 100 ? val.slice(0, 100) : val);
            }}
            autoComplete="off"
          />

          <div className="text-center">
            <button className="search-circle mt-1" type="submit">
              Submit
            </button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default SubDivisionCreateModal;